import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useContext, useState } from 'react';
import ManagementApi from '../../../apis/ManagementApi';
import SnackbarContext from '../../../contexts/SnackbarContext';
import useApi from '../../../lib/useApi';
import LoadingCircle from '../../LoadingCircle/LoadingCircle';
import DataRefreshContext from '../lib/DataRefreshContext';

interface InviteMembersDialogProps {
  showDialog: boolean;
  setShowDialog: (_: boolean) => void;
  reloadGrid: () => void;
}

const InviteMembersDialog = (props: InviteMembersDialogProps) => {
  const [membersToInvite, setMembersToInvite] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const managementApi = useApi(ManagementApi);
  const snackbar = useContext(SnackbarContext);

  // Access the triggerDataRefresh function from the context
  const { triggerDataRefresh } = useContext(DataRefreshContext);

  /**
   * Invite the members into the system
   */
  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    const submit = async () => {
      const response = await managementApi.InviteMembers(membersToInvite);

      if (response.code == 204) {
        snackbar.setAlert(
          <Alert variant="filled" severity="success">
            Successfully invited members
          </Alert>
        );
        props.setShowDialog(false);
        setMembersToInvite('');
        props.reloadGrid();

        // Trigger data refresh for other components
        triggerDataRefresh();
      } else {
        snackbar.setAlert(
          <Alert variant="filled" severity="error">
            {response.code == 400
              ? response.body
              : 'An unexpected error occurred while inviting members.'}
          </Alert>
        );
      }
      snackbar.triggerSnackbar();
    };
    submit().finally(() => {
      setIsLoading(false);
    });
  }, [managementApi, membersToInvite, props, snackbar, triggerDataRefresh]);

  return (
    <Dialog open={props.showDialog} onClose={() => setMembersToInvite('')}>
      <span style={{ position: 'relative' }}>
        <DialogTitle id="form-dialog-title">Invite Members</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography fontWeight="bold" gutterBottom>
              Provide a list of member emails to invite them to complete the
              training.
            </Typography>
            We will send each member an invitation to begin. You can enter the
            emails either on separate lines or separated by commas (CSV format).
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Member Emails"
            type="email"
            multiline
            rows={10}
            fullWidth
            onChange={(event) => setMembersToInvite(event.target.value)}
            value={membersToInvite}
            sx={{
              marginTop: '30px',
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              props.setShowDialog(false);
              setMembersToInvite('');
            }}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            id="invite-save-btn"
            variant="contained"
            onClick={handleSubmit}
          >
            Invite
          </Button>
        </DialogActions>
        <LoadingCircle
          isLoading={isLoading}
          loadingText="Inviting members..."
          loadingDelayMs={0}
        />
      </span>
    </Dialog>
  );
};

export default InviteMembersDialog;
